import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

export const generatePdfFileName = (
  type = "Quotation",
  person_name = "",
  company_name = "",
  quotation_number = "",
) => {
  const sanitize = (str) =>
    str
      ? String(str)
          .trim()
          .replace(/[\\/:*?"<>|]+/g, "")
          .replace(/\s+/g, "_")
      : "";

  const cleanPerson = sanitize(person_name);
  const cleanCompany = sanitize(company_name);
  const cleanNumber = sanitize(quotation_number);

  const parts = [type];
  if (cleanPerson) parts.push(cleanPerson);
  if (cleanCompany && cleanCompany.toLowerCase() !== cleanPerson.toLowerCase()) {
    parts.push(cleanCompany);
  }
  if (cleanNumber) parts.push(cleanNumber);

  return parts.join("-");
};


// FETCH QUOTATION STATS (cards, status / quick view counts)
export const fetchQuotationStats = createAsyncThunk(
  "quotations/fetchStats",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/quotations/stats", { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch quotation stats",
      );
    }
  },
);

// FETCH ALL QUOTATIONS
export const fetchAllQuotations = createAsyncThunk(
  "quotations/getAll",
  async (params, { signal, rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/quotations", { params, signal });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch quotations",
      );
    }
  },
);

// CREATE QUOTATION
export const addQuotation = createAsyncThunk(
  "quotations/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/quotations", payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// FETCH QUOTATION SIMPLIFIED LIST
export const fetchQuotationSimplifiedList = createAsyncThunk(
  "quotations/fetchQuotationSimplifiedList",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/quotations/quotation-simplified-list`, {
        params,
      });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch quotation simplified list",
      );
    }
  },
);

// FETCH SINGLE QUOTATION
export const getQuotationById = createAsyncThunk(
  "quotations/getById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/quotations/${id}`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch quotation",
      );
    }
  },
);

// UPDATE QUOTATION
export const updateQuotation = createAsyncThunk(
  "quotations/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/quotations/${id}`, payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// CREATE QUOTATION REVISION
export const addQuotationRevision = createAsyncThunk(
  "quotations/createRevision",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.post(
        `/tenant/quotations/${id}/revision`,
        payload,
      );
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// DELETE QUOTATION
export const deleteQuotation = createAsyncThunk(
  "quotations/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.delete(`/tenant/quotations/${id}`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to delete quotation",
      );
    }
  },
);

// BULK DELETE QUOTATIONS
export const bulkDeleteQuotations = createAsyncThunk(
  "quotations/bulkDelete",
  async (ids, { rejectWithValue }) => {
    try {
      const res = await Axios.delete("/tenant/quotations/bulk-delete", {
        data: { ids },
      });
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to delete quotations",
      );
    }
  },
);

// CHANGE QUOTATION STATUS
export const changeQuotationStatus = createAsyncThunk(
  "quotations/changeQuotationStatus",
  async ({ id, statusId }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        `/tenant/quotations/${id}/change-status`,
        {
          status_id: statusId,
        },
      );
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to change quotation status",
      );
    }
  },
);

// QUOTATION CONFIGURATION DATA
export const fetchQuotationConfigurationData = createAsyncThunk(
  "quotations/fetchConfigurationData",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/quotations/configurations", {
        params,
      });
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data?.data || {};
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to fetch quotation configurations",
      );
    }
  },
);
export const fetchUserListData = createAsyncThunk(
  "quotations/fetchUserListData",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/quotations/configurations/list", {
        params,
      });
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data?.data || {};
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to fetch quotation configurations",
      );
    }
  },
);

export const downloadQuotationPdf = createAsyncThunk(
  "quotations/downloadPdf",
  async (
    { id, quotation_number, person_name, company_name },
    { rejectWithValue },
  ) => {
    try {
      const response = await Axios.get(`/fast-merge-pdf?quotation_id=${id}`, {
        responseType: "blob",
      });

      let blob = response.data;

      // Smart Handling: JSON or PDF
      if (blob.type === "application/json") {
        const text = await blob.text();
        const json = JSON.parse(text);
        if (json.data?.pdf_base64) {
          const byteCharacters = atob(json.data.pdf_base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          blob = new Blob([new Uint8Array(byteNumbers)], {
            type: "application/pdf",
          });
        } else if (json.data?.pdf_url) {
          const pdfResponse = await fetch(json.data.pdf_url);
          blob = await pdfResponse.blob();
        }
      }

      if (blob.type !== "application/pdf") {
        return rejectWithValue("Failed to generate PDF content");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fileName = generatePdfFileName(
        "Quotation",
        person_name,
        company_name,
        quotation_number,
      );

      link.download = `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err.message || "Failed to download PDF",
      );
    }
  },
);

export const printQuotation = createAsyncThunk(
  "printQuotation/quotation",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/fast-merge-pdf?quotation_id=${id}`, {
        responseType: "blob",
      });

      let blob = response.data;

      // Smart Handling: JSON or PDF
      if (blob.type === "application/json") {
        const text = await blob.text();
        const json = JSON.parse(text);
        if (json.data?.pdf_base64) {
          const byteCharacters = atob(json.data.pdf_base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          blob = new Blob([new Uint8Array(byteNumbers)], {
            type: "application/pdf",
          });
        } else if (json.data?.pdf_url) {
          const pdfResponse = await fetch(json.data.pdf_url);
          blob = await pdfResponse.blob();
        }
      }

      if (blob && blob.type === "application/pdf") {
        const blobURL = window.URL.createObjectURL(blob);
        let iframe = document.getElementById("print-iframe");
        if (!iframe) {
          iframe = document.createElement("iframe");
          iframe.id = "print-iframe";
          iframe.style.display = "none";
          document.body.appendChild(iframe);
        }
        iframe.src = blobURL;

        iframe.onload = () => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        };
      } else {
        return rejectWithValue("Failed to generate PDF for printing.");
      }

      return { success: true, message: "Print dialog opened." };
    } catch (error) {
      return rejectWithValue(error.message || "Something went wrong");
    }
  },
);

export const previewQuotationPdf = createAsyncThunk(
  "quotations/previewPdf",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.post("/fast-quotation-preview", payload, {
        responseType: "blob",
      });

      let blob = response.data;
      let pdf_url = null;
      let pdf_base64 = null;

      // Smart Handling: JSON or PDF
      if (blob.type === "application/json") {
        const text = await blob.text();
        const json = JSON.parse(text);
        pdf_url = json.data?.pdf_url || json.pdf_url;
        pdf_base64 = json.data?.pdf_base64 || json.pdf_base64;

        if (pdf_base64) {
          const byteCharacters = atob(pdf_base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          blob = new Blob([new Uint8Array(byteNumbers)], {
            type: "application/pdf",
          });
        } else if (pdf_url) {
          const pdfResponse = await fetch(json.data.pdf_url);
          blob = await pdfResponse.blob();
        }
      }

      if (blob.type !== "application/pdf") {
        throw new Error("Failed to load PDF content");
      }

      return { blob, pdf_url, pdf_base64 };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Failed to generate preview",
      );
    }
  },
);

export const previewEstimationPdf = createAsyncThunk(
  "quotations/previewEstimationPdf",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.post("/tenant/quotations/preview-estimation", payload, {
        responseType: "blob",
      });

      let blob = response.data;
      let pdf_url = null;
      let pdf_base64 = null;

      if (blob.type === "application/json") {
        const text = await blob.text();
        const json = JSON.parse(text);
        pdf_url = json.data?.pdf_url || json.pdf_url;
        pdf_base64 = json.data?.pdf_base64 || json.pdf_base64;

        if (pdf_base64) {
          const byteCharacters = atob(pdf_base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          blob = new Blob([new Uint8Array(byteNumbers)], {
            type: "application/pdf",
          });
        } else if (pdf_url) {
          const pdfResponse = await fetch(json.data.pdf_url);
          blob = await pdfResponse.blob();
        }
      }

      if (blob.type !== "application/pdf") {
        throw new Error("Failed to load estimation PDF content");
      }

      return { blob, pdf_url, pdf_base64 };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Failed to generate estimation preview",
      );
    }
  },
);

export const viewEstimationPdf = createAsyncThunk(
  "quotations/viewEstimationPdf",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/tenant/quotations/${id}/view-estimation-pdf`, {
        responseType: "blob",
      });

      let blob = response.data;
      let pdf_url = null;
      let pdf_base64 = null;

      if (blob.type === "application/json") {
        const text = await blob.text();
        const json = JSON.parse(text);
        pdf_url = json.data?.pdf_url || json.pdf_url;
        pdf_base64 = json.data?.pdf_base64 || json.pdf_base64;

        if (pdf_base64) {
          const byteCharacters = atob(pdf_base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          blob = new Blob([new Uint8Array(byteNumbers)], {
            type: "application/pdf",
          });
        } else if (pdf_url) {
          const pdfResponse = await fetch(json.data.pdf_url);
          blob = await pdfResponse.blob();
        }
      }

      return { blob, pdf_url, pdf_base64 };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Failed to view estimation PDF",
      );
    }
  },
);

export const downloadEstimationPdf = createAsyncThunk(
  "quotations/downloadEstimationPdf",
  async (
    { id, quotation_number, person_name, company_name },
    { rejectWithValue },
  ) => {
    try {
      const response = await Axios.get(`/tenant/quotations/${id}/download-estimation-pdf`, {
        responseType: "blob",
      });

      let blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fileName = generatePdfFileName(
        "Estimation",
        person_name,
        company_name,
        quotation_number,
      );

      link.download = `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err.message || "Failed to download estimation PDF",
      );
    }
  },
);

// Turns a blob response (raw PDF, or JSON with pdf_base64 / pdf_url) into a PDF blob
const toPdfResult = async (responseBlob) => {
  let blob = responseBlob;
  let pdf_url = null;
  let pdf_base64 = null;

  if (blob.type === "application/json") {
    const json = JSON.parse(await blob.text());
    if (json.success === false) throw new Error(json.message || "Failed to generate PDF");
    pdf_url = json.data?.pdf_url || json.pdf_url;
    pdf_base64 = json.data?.pdf_base64 || json.pdf_base64;

    if (pdf_base64) {
      const byteCharacters = atob(pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
    } else if (pdf_url) {
      blob = await (await fetch(pdf_url)).blob();
    }
  }

  if (blob.type !== "application/pdf") {
    throw new Error("Failed to load power calculation PDF content");
  }

  return { blob, pdf_url, pdf_base64 };
};

export const previewPowerCalculationPdf = createAsyncThunk(
  "quotations/previewPowerCalculationPdf",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.post("/tenant/quotations/preview-power-calculation", payload, {
        responseType: "blob",
      });
      return await toPdfResult(response.data);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Failed to generate power calculation preview",
      );
    }
  },
);

export const viewPowerCalculationPdf = createAsyncThunk(
  "quotations/viewPowerCalculationPdf",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/tenant/quotations/${id}/view-power-calculation-pdf`, {
        responseType: "blob",
      });
      return await toPdfResult(response.data);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Failed to view power calculation PDF",
      );
    }
  },
);

export const downloadPowerCalculationPdf = createAsyncThunk(
  "quotations/downloadPowerCalculationPdf",
  async (
    { id, quotation_number, person_name, company_name },
    { rejectWithValue },
  ) => {
    try {
      const response = await Axios.get(`/tenant/quotations/${id}/download-power-calculation-pdf`, {
        responseType: "blob",
      });

      const { blob } = await toPdfResult(response.data);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fileName = generatePdfFileName(
        "Power_Calculation",
        person_name,
        company_name,
        quotation_number,
      );

      link.download = `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err.message || "Failed to download power calculation PDF",
      );
    }
  },
);

// GET QUOTATION HISTORY
export const getQuotationHistory = createAsyncThunk(
  "quotations/getHistory",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(
        `/tenant/quotation-histories?quotation_id=${id}`,
      );
      return res.data.data.data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to fetch history");
    }
  },
);

// VIEW SAVED QUOTATION PDF (Uses Fast API GET)
export const viewQuotationPdf = createAsyncThunk(
  "quotations/viewPdf",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/fast-quotation-view/${id}`, {
        responseType: "blob",
      });

      if (response.data.type === "application/pdf") {
        return { blob: response.data };
      } else {
        const text = await response.data.text();
        const json = JSON.parse(text);
        return rejectWithValue(json.message || "Failed to load PDF");
      }
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || err.message || "Failed to load PDF",
      );
    }
  },
);
const quotationSlice = createSlice({
  name: "quotations",
  initialState: {
    quotations: [],
    selectedQuotation: null,
    total_records: 0,
    isQuotationsLoading: false,
    stats: null,
    isStatsLoading: false,
    isConfigLoading: false,
    quotationHistories: [],
    error: null,
    userListData: {
      assigned_to: [],
    },
    configurationData: {
      assigned_to: [],
      statuses: [],
      packages: [],
      products: [],
      delivery_terms: "",
      payment_terms: "",
      other_terms: "",
      terms_and_conditions: "",
      taxes: "",
    },
    currentRequestId: null,
  },

  extraReducers: (builder) => {
    builder
      // STATS
      .addCase(fetchQuotationStats.pending, (state) => {
        state.isStatsLoading = true;
      })
      .addCase(fetchQuotationStats.fulfilled, (state, action) => {
        state.isStatsLoading = false;
        state.stats = action.payload || state.stats;
      })
      .addCase(fetchQuotationStats.rejected, (state) => {
        state.isStatsLoading = false;
      })

      // FETCH ALL
      .addCase(fetchAllQuotations.pending, (state, action) => {
        state.isQuotationsLoading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchAllQuotations.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isQuotationsLoading = false;
        state.quotations = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchAllQuotations.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.isQuotationsLoading = false;
        state.error = action.payload;
      })

      // FETCH CONFIGURATION DATA
      .addCase(fetchQuotationConfigurationData.pending, (state) => {
        state.isConfigLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotationConfigurationData.fulfilled, (state, action) => {
        state.isConfigLoading = false;
        state.configurationData = action.payload || {};
      })
      .addCase(fetchQuotationConfigurationData.rejected, (state, action) => {
        state.isConfigLoading = false;
        state.error = action.payload;
      })

      // FETCH USER LIST DATA
      .addCase(fetchUserListData.pending, (state) => {
        state.isConfigLoading = true;
        state.error = null;
      })
      .addCase(fetchUserListData.fulfilled, (state, action) => {
        state.isConfigLoading = false;
        state.userListData = action.payload || {};
        state.configurationData = {
          ...state.configurationData,
          ...action.payload,
        };
      })
      .addCase(fetchUserListData.rejected, (state, action) => {
        state.isConfigLoading = false;
        state.error = action.payload;
      })

      // FETCH BY ID (doesn't touch isQuotationsLoading, which drives the list's loader)
      .addCase(getQuotationById.pending, (state) => {
        state.selectedQuotation = null;
        state.error = null;
      })
      .addCase(getQuotationById.fulfilled, (state, action) => {
        state.selectedQuotation = action.payload;
      })
      .addCase(getQuotationById.rejected, (state, action) => {
        state.error = action.payload;
      })

      // GET QUOTATION HISTORY
      .addCase(getQuotationHistory.pending, (state) => {
        state.error = null;
      })
      .addCase(getQuotationHistory.fulfilled, (state, action) => {
        state.quotationHistories = action.payload;
      })
      .addCase(getQuotationHistory.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export default quotationSlice.reducer;
